// Operaciones PURAS sobre el anexo técnico del documento al cliente (F4b · D1). Sin estado de página: cada función
// recibe el anexo y devuelve otro (o el mismo, si la operación no corresponde: en un tope o en un borde).
// Los límites son un espejo de `backend/src/shared/pricer-datec-anexo.ts`; un test los compara.
import type {
  AnexoTecnico,
  BloqueAnexo,
  SeccionAnexo,
  TablaDeAnexo,
  TipoDeSeccion,
} from '../types/pricer-datec.types';

export const LIMITES_ANEXO = {
  titulo: 120,
  subtitulo: 160,
  nombreDeBloque: 80,
  tituloDeSeccion: 120,
  seccionesPorBloque: 20,
  seccionesPorAnexo: 20,
  bloquesPorAnexo: 10,
  bloquesEnBiblioteca: 20,
  parrafo: 3000,
  vinetaMaxima: 300,
  vinetasMaximas: 30,
  columnasMaximas: 6,
  encabezadoDeColumna: 60,
  filasMaximas: 30,
  celda: 400,
} as const;

export const NOMBRE_DEL_TIPO: Record<TipoDeSeccion, string> = {
  PARRAFO: 'Párrafo',
  VINETAS: 'Viñetas',
  TABLA: 'Tabla',
};

type NuevoId = () => string;

export const idNuevo: NuevoId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : '00000000-0000-4000-8000-' + Math.random().toString(16).slice(2, 14).padEnd(12, '0');

const tablaNueva = (): TablaDeAnexo => ({
  columnas: ['Columna 1', 'Columna 2'],
  filas: [['', '']],
});

export function seccionEnBlanco(tipo: TipoDeSeccion, nuevoId: NuevoId = idNuevo): SeccionAnexo {
  return cambiarTipo({ id: nuevoId(), titulo: '', tipo: 'PARRAFO', texto: '' }, tipo);
}

export function bloqueEnBlanco(nombre: string, nuevoId: NuevoId = idNuevo): BloqueAnexo {
  return { id: nuevoId(), nombre, secciones: [] };
}

/** Una copia del bloque con identidades NUEVAS (al agregarlo dos veces los ids no se repiten). */
export function copiarBloque(b: BloqueAnexo, nuevoId: NuevoId = idNuevo): BloqueAnexo {
  const c = structuredClone(b);
  return { ...c, id: nuevoId(), secciones: c.secciones.map((s) => ({ ...s, id: nuevoId() })) };
}

export function totalDeSecciones(a: Pick<AnexoTecnico, 'bloques'>): number {
  return a.bloques.reduce((n, b) => n + b.secciones.length, 0);
}

/** Cambia el tipo de una sección conservando título y texto, y arma lo que el tipo nuevo necesita. */
export function cambiarTipo(s: SeccionAnexo, tipo: TipoDeSeccion): SeccionAnexo {
  const { id, titulo, texto } = s;
  if (tipo === 'PARRAFO') return { id, titulo, texto, tipo };
  if (tipo === 'VINETAS') {
    return { id, titulo, texto, tipo, vinetas: s.vinetas?.length ? s.vinetas : [''] };
  }
  return { id, titulo, texto, tipo, tabla: s.tabla ?? tablaNueva() };
}

const reemplazarBloque = (a: AnexoTecnico, i: number, b: BloqueAnexo): AnexoTecnico => ({
  ...a,
  bloques: a.bloques.map((x, k) => (k === i ? b : x)),
});

// ── bloques ─────────────────────────────────────────────────────────────────

/** Agrega un bloque al final; en el tope de bloques o de secciones no hace nada. */
export function agregarBloque(
  a: AnexoTecnico,
  b: BloqueAnexo,
  opciones: { biblioteca?: boolean } = {}
): AnexoTecnico {
  const tope = opciones.biblioteca
    ? LIMITES_ANEXO.bloquesEnBiblioteca
    : LIMITES_ANEXO.bloquesPorAnexo;
  if (a.bloques.length >= tope) return a;
  // La biblioteca no suma secciones entre bloques: el tope total es del anexo de una cotización.
  if (
    !opciones.biblioteca &&
    totalDeSecciones(a) + b.secciones.length > LIMITES_ANEXO.seccionesPorAnexo
  ) {
    return a;
  }
  return { ...a, bloques: [...a.bloques, b] };
}

export function quitarBloque(a: AnexoTecnico, i: number): AnexoTecnico {
  return { ...a, bloques: a.bloques.filter((_, k) => k !== i) };
}

function mover<T>(lista: T[], i: number, paso: -1 | 1): T[] | null {
  const j = i + paso;
  if (i < 0 || i >= lista.length || j < 0 || j >= lista.length) return null;
  const c = lista.slice();
  [c[i], c[j]] = [c[j], c[i]];
  return c;
}

export function moverBloque(a: AnexoTecnico, i: number, paso: -1 | 1): AnexoTecnico {
  const l = mover(a.bloques, i, paso);
  return l ? { ...a, bloques: l } : a;
}

/** Lleva el bloque `desde` a la posición `hacia` (arrastrar y soltar). Índices fuera de rango o iguales no cambian nada. */
export function reordenarBloque(a: AnexoTecnico, desde: number, hacia: number): AnexoTecnico {
  const n = a.bloques.length;
  if (desde === hacia || desde < 0 || hacia < 0 || desde >= n || hacia >= n) return a;
  const c = a.bloques.slice();
  const [b] = c.splice(desde, 1);
  c.splice(hacia, 0, b);
  return { ...a, bloques: c };
}

export function renombrarBloque(a: AnexoTecnico, i: number, nombre: string): AnexoTecnico {
  return reemplazarBloque(a, i, { ...a.bloques[i], nombre });
}

// ── secciones ───────────────────────────────────────────────────────────────

export function agregarSeccion(
  a: AnexoTecnico,
  bloque: number,
  s: SeccionAnexo,
  opciones: { biblioteca?: boolean } = {}
): AnexoTecnico {
  const b = a.bloques[bloque];
  if (!b) return a;
  if (b.secciones.length >= LIMITES_ANEXO.seccionesPorBloque) return a;
  if (!opciones.biblioteca && totalDeSecciones(a) >= LIMITES_ANEXO.seccionesPorAnexo) return a;
  return reemplazarBloque(a, bloque, { ...b, secciones: [...b.secciones, s] });
}

export function quitarSeccion(a: AnexoTecnico, bloque: number, i: number): AnexoTecnico {
  const b = a.bloques[bloque];
  if (!b) return a;
  return reemplazarBloque(a, bloque, { ...b, secciones: b.secciones.filter((_, k) => k !== i) });
}

export function moverSeccion(
  a: AnexoTecnico,
  bloque: number,
  i: number,
  paso: -1 | 1
): AnexoTecnico {
  const b = a.bloques[bloque];
  const l = b ? mover(b.secciones, i, paso) : null;
  return b && l ? reemplazarBloque(a, bloque, { ...b, secciones: l }) : a;
}

export function actualizarSeccion(
  a: AnexoTecnico,
  bloque: number,
  i: number,
  cambio: Partial<SeccionAnexo>
): AnexoTecnico {
  const b = a.bloques[bloque];
  if (!b || !b.secciones[i]) return a;
  const secciones = b.secciones.map((s, k) =>
    k === i ? ({ ...s, ...cambio } as SeccionAnexo) : s
  );
  return reemplazarBloque(a, bloque, { ...b, secciones });
}

// ── tabla ───────────────────────────────────────────────────────────────────

export function agregarColumna(t: TablaDeAnexo): TablaDeAnexo {
  if (t.columnas.length >= LIMITES_ANEXO.columnasMaximas) return t;
  return {
    columnas: [...t.columnas, `Columna ${t.columnas.length + 1}`],
    filas: t.filas.map((f) => [...f, '']),
  };
}

export function quitarColumna(t: TablaDeAnexo, i: number): TablaDeAnexo {
  if (t.columnas.length <= 1 || i < 0 || i >= t.columnas.length) return t;
  return {
    columnas: t.columnas.filter((_, k) => k !== i),
    filas: t.filas.map((f) => f.filter((_, k) => k !== i)),
  };
}

export function agregarFila(t: TablaDeAnexo): TablaDeAnexo {
  if (t.filas.length >= LIMITES_ANEXO.filasMaximas) return t;
  return { ...t, filas: [...t.filas, t.columnas.map(() => '')] };
}

export function quitarFila(t: TablaDeAnexo, i: number): TablaDeAnexo {
  if (t.filas.length <= 1 || i < 0 || i >= t.filas.length) return t;
  return { ...t, filas: t.filas.filter((_, k) => k !== i) };
}

// ── numeración y validación ─────────────────────────────────────────────────

/** 01, 02, … por el orden de TODAS las secciones del anexo (no se guarda: sale del orden). */
export function numerarSecciones(a: Pick<AnexoTecnico, 'bloques'>): Record<string, string> {
  const r: Record<string, string> = {};
  let n = 0;
  for (const b of a.bloques) for (const s of b.secciones) r[s.id] = String(++n).padStart(2, '0');
  return r;
}

/** Lo que el servidor rechazaría, en español claro (para avisar antes de guardar). */
export function problemasDelAnexo(a: AnexoTecnico): string[] {
  const p: string[] = [];
  const L = LIMITES_ANEXO;
  if (a.titulo.length > L.titulo)
    p.push(`El título del anexo admite hasta ${L.titulo} caracteres.`);
  if (a.subtitulo.length > L.subtitulo) {
    p.push(`El subtítulo admite hasta ${L.subtitulo} caracteres.`);
  }
  if (totalDeSecciones(a) > L.seccionesPorAnexo) {
    p.push(`El anexo admite hasta ${L.seccionesPorAnexo} secciones en total.`);
  }
  a.bloques.forEach((b, i) => {
    const donde = `Bloque ${i + 1}`;
    if (!b.nombre.trim()) p.push(`${donde}: falta el nombre del bloque.`);
    if (b.nombre.length > L.nombreDeBloque) {
      p.push(`${donde}: el nombre admite hasta ${L.nombreDeBloque} caracteres.`);
    }
    b.secciones.forEach((s, k) => {
      const aqui = `${donde}, sección ${k + 1}`;
      if (!s.titulo.trim()) p.push(`${aqui}: falta el título de la sección.`);
      if (s.titulo.length > L.tituloDeSeccion) {
        p.push(`${aqui}: el título admite hasta ${L.tituloDeSeccion} caracteres.`);
      }
      if (s.texto.length > L.parrafo) {
        p.push(`${aqui}: el texto admite hasta ${L.parrafo} caracteres.`);
      }
      if (s.tipo === 'VINETAS') {
        const v = s.vinetas ?? [];
        if (v.length === 0 || v.some((x) => !x.trim())) p.push(`${aqui}: hay una viñeta vacía.`);
        if (v.some((x) => x.length > L.vinetaMaxima)) {
          p.push(`${aqui}: una viñeta admite hasta ${L.vinetaMaxima} caracteres.`);
        }
      }
      if (s.tipo === 'TABLA') {
        const t = s.tabla;
        if (!t || t.columnas.length === 0) {
          p.push(`${aqui}: la tabla necesita al menos una columna.`);
        } else {
          if (t.columnas.some((c) => !c.trim())) p.push(`${aqui}: falta el nombre de una columna.`);
          if (t.filas.some((f) => f.some((c) => c.length > L.celda))) {
            p.push(`${aqui}: una celda admite hasta ${L.celda} caracteres.`);
          }
        }
      }
    });
  });
  return p;
}
